/**
 * 현장 방송 상태 — 경보 1건의 `delivery` 원장에서 읽는다(2026-10-19 · 10-12 검증 V-20/V-22, 경보창 표시용).
 *
 * 새 상태를 만들지 않는다. 서버 `safety/services/notify/tts.py` 가 이미 적는 것을 읽기만 한다:
 *   · `tts:<lang>` — 언어별 방송 명령. 적재 때 queued, 현장 PC(매니져) ack 가 오면 done/error/unsupported/
 *     cancelled/suppressed 로 바뀐다(ack_broadcast).
 *   · `tts` — 언어별 요약(_summarize). 하나라도 queued 면 queued.
 * `tts` 가 없으면 이 경보에는 현장 방송이 없다(방송 장비 없는 현장·옛 경보) → null 을 돌려주고 아무것도 안 그린다.
 * `tts` 가 `skipped` 이고 언어 칸이 없어도 같다 — 서버가 일부러 방송하지 않은 경보다(모의훈련 「외부 발송 억제」,
 * 비가스·설비 무응답, 통신 복구 행, 해제·늦은 기록). 이것을 「방송 실패」로 그리면 안 된다(10-19 검증).
 *
 * ⛔ queued 에는 **시간 창을 건다**(SiteFloorsPanel.isBroadcasting 과 같은 120초 + 여유 15초). 매니져가 끝내 ack 하지
 * 않으면 queued 가 영원히 남아 몇 시간 전 방송이 「방송 중」으로 보인다 — 창이 지나면 「응답 없음」으로 바꾼다.
 * 창은 **서버 시각**으로 잰다(2026-10-20, F1 검증 잔존 결함 2): 적재 시각(at)은 서버 시계다. 기기 시계로 재면 시계가
 * 늦은 단말(RTC 없는 키오스크·RPi 콘솔)은 「방송 중」이 풀리지 않고, 빠른 단말은 방금 낸 방송이 「응답 없음」으로 보였다.
 * 호출자가 시계 차(기기 − 서버, 응답 Date 헤더로 잰 serverClockSkewFromHeader)를 주면 그만큼 보정한다. 없으면 기기
 * 시계로 재되, 적재 시각이 기기 시계보다 {@link FUTURE_TOLERANCE_MS} 넘게 미래면(= 기기 시계가 늦다) 판정하지 않는다 →
 * `unknown`(「방송 중」도 「응답 없음」도 아님). 보정값 없이 시계가 빠른 단말은 알아챌 수 없다(남은 한계).
 *
 * 출력별 결과(PC 스피커 / IP 스피커)는 **매니져 ack 가 실어 줄 때만** 보인다. 지금 매니져(3.0.20x)는 PC 결과만 보고
 * ack 하고 IP 결과는 로그에만 남긴다(P1-12 매니져 작업에서 추가 예정). 그때 쓸 모양을 둘 다 받는다:
 *   1. 원장 칸에 `outputs: { pc, ip }` 필드(서버가 구조로 저장해 줄 때), 또는
 *   2. ack detail 글 안의 `PC:ok` · `IP:fail` · `IP:1/2` 같은 꼬리표(서버 변경 없이 매니져만 바꿀 때).
 * 둘 다 없으면 출력별 칸은 그리지 않는다(구버전 — 「모름」을 「정상」으로 보이지 않게).
 */

export type BroadcastState =
  | "queued"
  | "done"
  | "error"
  | "cancelled"
  | "suppressed"
  | "unsupported"
  | "stale"
  /** 판정 불가 — 시계 보정값이 없는데 적재 시각이 기기 시계보다 미래(기기 시계가 늦다). */
  | "unknown";

/** 출력 하나의 결과. partial = 여러 대 중 일부만(IP 스피커 1/2 등). */
export type OutputState = "ok" | "partial" | "fail" | "off";

export interface BroadcastSummary {
  state: BroadcastState;
  /** 재생 확인(done)된 언어 수 / 방송 명령을 낸 언어 수. */
  langsDone: number;
  langsTotal: number;
  /** PC 스피커 — 매니져가 출력별 결과를 안 보냈으면 null. */
  pc: OutputState | null;
  /** IP 스피커 — 매니져가 출력별 결과를 안 보냈으면 null. */
  ip: OutputState | null;
}

/** = 백엔드 RELAY_COMMAND_TTL_SECONDS(120) + ack 왕복·폴링 여유. SiteFloorsPanel 과 같은 값. */
const BROADCAST_STALE_MS = 120_000 + 15_000;
/**
 * 적재 시각이 「지금」보다 미래여도 그대로 재 주는 폭 — 시계 오차·Date 헤더 초 단위 잘림만큼(alertBaseline 과 같은 1분).
 * 이보다 더 미래면 판정하지 않는다(unknown).
 */
const FUTURE_TOLERANCE_MS = 60_000;

type Entry = { status?: unknown; detail?: unknown; at?: unknown; outputs?: unknown; pc?: unknown; ip?: unknown };

const KNOWN: ReadonlySet<string> = new Set(["queued", "done", "error", "cancelled", "suppressed", "unsupported"]);

function normOutput(raw: unknown): OutputState | null {
  if (typeof raw !== "string") return null;
  const s = raw.trim().toLowerCase();
  const frac = s.match(/^(\d+)\s*\/\s*(\d+)$/);
  if (frac) {
    const ok = Number(frac[1]);
    const all = Number(frac[2]);
    if (!all) return "off";
    return ok >= all ? "ok" : ok <= 0 ? "fail" : "partial";
  }
  if (["ok", "done", "success", "on", "성공", "정상"].includes(s)) return "ok";
  if (["partial", "part", "일부"].includes(s)) return "partial";
  if (["fail", "failed", "error", "실패"].includes(s)) return "fail";
  if (["off", "none", "skip", "skipped", "disabled", "꺼짐", "없음"].includes(s)) return "off";
  return null;
}

/** 원장 칸 하나에서 (pc, ip) 결과를 꺼낸다 — 구조 필드 우선, 없으면 detail 꼬리표. */
function outputsOf(e: Entry): { pc: OutputState | null; ip: OutputState | null } {
  const o = e.outputs && typeof e.outputs === "object" ? (e.outputs as Record<string, unknown>) : null;
  let pc = normOutput(o?.pc ?? e.pc);
  let ip = normOutput(o?.ip ?? e.ip);
  if ((pc == null || ip == null) && typeof e.detail === "string") {
    const d = e.detail;
    if (pc == null) pc = normOutput(d.match(/\bPC\s*[:=]\s*([A-Za-z]+|\d+\s*\/\s*\d+)/i)?.[1]);
    if (ip == null) ip = normOutput(d.match(/\bIP\s*[:=]\s*([A-Za-z]+|\d+\s*\/\s*\d+)/i)?.[1]);
  }
  return { pc, ip };
}

/** 언어별 결과를 하나로 — 전부 같으면 그 값, 섞이면 partial(꺼짐은 결과에서 뺀다). */
function merge(states: (OutputState | null)[]): OutputState | null {
  const known = states.filter((s): s is OutputState => s != null);
  if (!known.length) return null;
  const live = known.filter((s) => s !== "off");
  if (!live.length) return "off";
  if (live.every((s) => s === "ok")) return "ok";
  if (live.every((s) => s === "fail")) return "fail";
  return "partial";
}

/**
 * 요약 칸을 못 믿을 때(없음·skipped·모르는 값) 언어 칸으로 다시 집계 — 서버 tts._summarize 와 같은 규칙.
 * 해제로 끊긴 것(cancelled)·현장 PC 가 일부러 생략한 것(suppressed)은 실패로 세지 않는다.
 */
function summarizePerLang(perLang: Entry[]): BroadcastState {
  const n = (s: string) => perLang.filter((e) => e.status === s).length;
  const total = perLang.length;
  const done = n("done");
  const cancelled = n("cancelled");
  const sup = n("suppressed");
  if (n("queued")) return "queued";
  if (done === total) return "done";
  if (cancelled && done + cancelled + sup === total) return "cancelled";
  if (sup && done + sup === total) return "suppressed";
  return "error";
}

/**
 * 경보 1건의 현장 방송 요약. 현장 방송 기록이 없거나 서버가 방송하지 않은 경보(skipped)면 null.
 * @param now 기기 시계(ms).
 * @param clockSkewMs 「기기 시계 − 서버 시계」(ms) — 모르면 null(기본). queued 의 응답 없음 창을 서버 시각으로 재는 데만 쓴다.
 */
export function summarizeBroadcast(
  delivery: unknown,
  now: number,
  clockSkewMs: number | null = null,
): BroadcastSummary | null {
  if (!delivery || typeof delivery !== "object") return null;
  const d = delivery as Record<string, unknown>;
  const perLang = Object.entries(d)
    .filter(([k, v]) => k.startsWith("tts:") && v && typeof v === "object")
    .map(([, v]) => v as Entry);
  const head = d.tts && typeof d.tts === "object" ? (d.tts as Entry) : null;
  if (!head && !perLang.length) return null;

  const raw = typeof head?.status === "string" ? head.status : "";
  let state: BroadcastState;
  if (KNOWN.has(raw)) state = raw as BroadcastState;
  // 방송 명령을 낸 언어가 없는데 요약도 아는 상태가 아니다 — skipped(일부러 방송 안 함) 또는 모르는 값. 「모름」을
  // 「실패」로 보이지 않게 그리지 않는다.
  else if (!perLang.length) return null;
  else state = summarizePerLang(perLang);

  if (state === "queued") {
    // 요약 시각이 없으면 가장 늦은 언어 칸 시각으로 본다. 둘 다 못 읽으면 판단하지 않는다(queued 유지).
    const times = [head?.at, ...perLang.map((e) => e.at)]
      .map((t) => (typeof t === "string" ? Date.parse(t) : NaN))
      .filter((t) => !Number.isNaN(t));
    if (times.length) {
      // 서버 시각으로 잰다 — 보정값이 있으면 기기 시계에서 시계 차를 뺀다(맨 위 주석).
      const serverNow = clockSkewMs != null && Number.isFinite(clockSkewMs) ? now - clockSkewMs : now;
      const age = serverNow - Math.max(...times);
      if (age < -FUTURE_TOLERANCE_MS) state = "unknown";
      else if (age > BROADCAST_STALE_MS) state = "stale";
    }
  }

  const outs = perLang.map(outputsOf);
  return {
    state,
    langsDone: perLang.filter((e) => e.status === "done").length,
    langsTotal: perLang.length,
    pc: merge(outs.map((o) => o.pc)),
    ip: merge(outs.map((o) => o.ip)),
  };
}
